import {
  Button,
  Checkbox,
  Group,
  MultiSelect,
  Select,
  Stack,
  TextInput,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useState } from "react";

import {
  dayRangeEndToISO,
  dayRangeStartToISO,
} from "../../../../../utils/datetime";

import {
  JournalActorType,
  JournalCategory,
  JournalSeverity,
} from "../../../../../gql/graphql";
import type { JournalFiltersInput } from "../hooks/useJournalLiveTail";

/**
 * Option lists derived from the schema-generated enums so the UI never
 * offers a value the schema can't accept and the picker stays in sync
 * automatically when the backend evolves the enum.
 */
const JOURNAL_CATEGORY_VALUES = Object.values(JournalCategory);
const JOURNAL_SEVERITY_VALUES = Object.values(JournalSeverity);
const JOURNAL_ACTOR_TYPE_VALUES = Object.values(JournalActorType);

function narrowCategories(values: string[]): JournalCategory[] {
  const allowed: readonly string[] = JOURNAL_CATEGORY_VALUES;
  return values.filter((v): v is JournalCategory => allowed.includes(v));
}

function narrowSeverities(values: string[]): JournalSeverity[] {
  const allowed: readonly string[] = JOURNAL_SEVERITY_VALUES;
  return values.filter((v): v is JournalSeverity => allowed.includes(v));
}

function narrowActorType(value: string | null): JournalActorType | undefined {
  if (value === null) return undefined;
  const allowed: readonly string[] = JOURNAL_ACTOR_TYPE_VALUES;
  return allowed.includes(value) ? (value as JournalActorType) : undefined;
}

interface Props {
  defaultFilters: JournalFiltersInput;
  onApply: (filters: JournalFiltersInput) => void;
}

export function JournalFilterBar({ defaultFilters, onApply }: Props) {
  const [filters, setFilters] = useState<JournalFiltersInput>(defaultFilters);

  const dateRangeValue: [string | null, string | null] = [
    typeof filters.createdAfter === "string" ? filters.createdAfter : null,
    typeof filters.createdBefore === "string" ? filters.createdBefore : null,
  ];

  return (
    <Stack gap="xs">
      <Group wrap="wrap" gap="sm" align="flex-end">
        <MultiSelect
          label="Categories"
          data={JOURNAL_CATEGORY_VALUES}
          value={filters.categories ?? []}
          onChange={(values) =>
            setFilters({ ...filters, categories: narrowCategories(values) })
          }
          w={200}
        />
        <MultiSelect
          label="Severity"
          data={JOURNAL_SEVERITY_VALUES}
          value={filters.severity ?? []}
          onChange={(values) =>
            setFilters({ ...filters, severity: narrowSeverities(values) })
          }
          w={200}
        />
        <TextInput
          label="Type prefix"
          placeholder="rule_engine."
          value={filters.typePrefix ?? ""}
          onChange={(e) =>
            setFilters({
              ...filters,
              typePrefix: e.target.value === "" ? undefined : e.target.value,
            })
          }
          w={200}
        />
        <Select
          label="Actor type"
          data={JOURNAL_ACTOR_TYPE_VALUES}
          value={filters.actorType ?? null}
          onChange={(value) =>
            setFilters({ ...filters, actorType: narrowActorType(value) })
          }
          clearable
          w={200}
        />
        <DatePickerInput
          type="range"
          label="Time range"
          value={dateRangeValue}
          onChange={(value) => {
            const [after, before] = value;
            setFilters({
              ...filters,
              createdAfter: dayRangeStartToISO(after) || undefined,
              createdBefore: dayRangeEndToISO(before) || undefined,
            });
          }}
          w={260}
          clearable
        />
        <Checkbox
          label="Include simulations"
          checked={filters.includeSimulations ?? false}
          onChange={(e) =>
            setFilters({
              ...filters,
              includeSimulations: e.currentTarget.checked,
            })
          }
        />
      </Group>
      <Group>
        <Button onClick={() => onApply(filters)}>Apply</Button>
        <Button
          variant="default"
          onClick={() => {
            setFilters(defaultFilters);
            onApply(defaultFilters);
          }}
        >
          Reset
        </Button>
      </Group>
    </Stack>
  );
}
